'use client'

import { useEffect, useState } from 'react'
import type { ChangeEvent } from 'react'
import { ChevronDown } from 'lucide-react'
import clsx from 'clsx'

import Error from '/react4xp/components/prototype/Form/Error'
import { useFormField } from '/react4xp/utils/prototype/useFormField'

import type { SelectProps } from './types'

export default function Select({
	id,
	name,
	label,
	floatingLabel = false,
	options,
	defaultSelection,
	required = false,
	error: externalError,
	invalid: externalInvalid,
	disabled = false,
	className,
	onChange: externalOnChange,
	requiredMessage,
	...props
}: SelectProps) {
	const { finalError, finalInvalid, updateFormValue, getRegisterProps } = useFormField({
		name,
		externalError,
		externalInvalid,
		required,
		requiredMessage,
	})
	const errorId = `${id}-error`
	const [value, setValue] = useState<string>(defaultSelection || options[0]?.value || '')

	const registerProps = getRegisterProps({
		validate: (val: string) => {
			if (!required) return true
			const selected = options.find((o) => o.value === val)
			return !selected?.disabled || (requiredMessage ?? 'Dette feltet er påkrevd')
		},
	})

	useEffect(() => {
		if (value) updateFormValue(value, { shouldValidate: false })
	}, [])

	useEffect(() => {
		if (defaultSelection !== undefined) {
			setValue(defaultSelection)
			updateFormValue(defaultSelection, { shouldValidate: false })
		}
	}, [defaultSelection])

	function handleChange(e: ChangeEvent<HTMLSelectElement>) {
		setValue(e.target.value)
		externalOnChange?.(e.target.value)
		updateFormValue(e.target.value)
	}

	return (
		<div className={clsx('form-field', className)}>
			{label && !floatingLabel && (
				<label htmlFor={id} className="form-label">
					{label}
					{required && (
						<span aria-hidden="true" className="form-label-required">
							*
						</span>
					)}
				</label>
			)}
			<div className="form-text-field">
				<select
					{...registerProps}
					id={id}
					name={name}
					required={required}
					disabled={disabled}
					value={value}
					aria-invalid={finalInvalid || undefined}
					aria-describedby={finalError ? errorId : undefined}
					className={clsx('form-text-input form-select', floatingLabel && 'peer')}
					onChange={handleChange}
					{...props}
				>
					{options.map((option) => (
						<option key={option.value} value={option.value} disabled={option.disabled}>
							{option.label}
						</option>
					))}
				</select>
				{label && floatingLabel && (
					<label htmlFor={id} className="form-floating-label form-floating-label--floated">
						{label}
						{required && (
							<span aria-hidden="true" className="ml-0.5">
								*
							</span>
						)}
					</label>
				)}
				<ChevronDown strokeWidth={1.25} className="form-select-chevron" />
			</div>
			{finalError && finalInvalid && <Error label={finalError} id={errorId} />}
		</div>
	)
}
